import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import Select from 'react/form/select_controlled';
import styles from './admin_components.module.scss';
import Modal from '../../form/modal';
import TextInput from '../../form/text';
import NumberInput from '../../form/number';
import CopyToClipboard from 'components/CopyToClipboard';
import DatePicker from 'react-datepicker';
import Link from 'components/Link';
import 'react-datepicker/dist/react-datepicker.css';
import TeamImage from 'components/TeamImage';
import Clickable from '@/components/Clickable';

const SBLContentContainer = ({ title, children, className, ...otherProps }) => (
  <div className={[styles.SBLContentContainer, className].join(' ')} {...otherProps}>
    <div className={styles.ContainerTitle}>{title}</div>
    {children}
  </div>
);

const SBLSelector = ({ value, label, onChange, options, name, className, ...otherProps }) => (
  <div className={className}>
    <div className={styles.SBLLabel}>{label}</div>
    <Select className={styles.SBLSelector} value={value} onChange={onChange} options={options} name={name} {...otherProps} />
  </div>
);

const SBLDateTime = ({ value, label, type, onChange, className, otherProps }) => {
  const dateFormat = type === 'date';
  const dateValue = value ? new Date(value) : null;
  const timeValue = value ? new Date(value) : new Date().setHours(12, 0, 0, 0);
  const classes = [styles.SBLDateTime];
  if (dateFormat) classes.push(styles.SBLDate);

  function handleChange(date) {
    if (date && date.getFullYear() === 2001) {
      const todaysDate = dayjs();
      const newDate = dayjs(date).year(todaysDate.year());
      if (todaysDate.diff(newDate, 'day') < 30) {
        date.setFullYear(todaysDate.year());
      } else {
        date.setFullYear(todaysDate.year() + 1);
      }
    }
    onChange(date);
  }

  return (
    <div className={className}>
      <div className={styles.SBLLabel}>{label}</div>
      {dateFormat ? (
        <DatePicker
          name={type}
          className={classes.join(' ')}
          selected={dateValue}
          placeholderText="Select Date"
          onChange={handleChange}
          dateFormat="MMM d, yyyy"
        />
      ) : (
        <DatePicker
          className={classes.join(' ')}
          placeholderText="Select Time"
          selected={timeValue}
          onChange={handleChange}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={15}
          timeCaption="Time"
          dateFormat="h:mm aa"
        />
      )}
    </div>
  );
};

const SBLTeamBlock = ({ name, mascot, abbrev, img, mobileAbbrev, home, away }) => (
  <div className={styles.SBLTeamBlock}>
    {home ? <div className={styles.SBLTeamBlockVS}>vs</div> : null}
    {away ? <div className={styles.SBLTeamBlockVS}>@</div> : null}
    <TeamImage src={img} alt={name} />
    {mobileAbbrev ? (
      <React.Fragment>
        <div className={styles.hideMobile}>
          {name} {mascot}
        </div>
        <div className={styles.hideDesktop}>{abbrev}</div>
      </React.Fragment>
    ) : (
      <div className={styles.SBLTeamBlockName}>
        {name} {mascot}
      </div>
    )}
  </div>
);

const SBLTeamImageGroup = ({ isIcon, isLogo, genderSport, imgSrc, name, level, url }) => (
  <div className={styles.SBLTeamImageGroup}>
    {isIcon && <i className={`icon ${genderSport.slug}-icon`} />}
    {isLogo && <TeamImage src={imgSrc} alt={name} />}
    <Clickable href={url}>
      <div className={styles.IGTopText}>{name}</div>
      <div className={styles.IGBottomText}>{level}</div>
    </Clickable>
  </div>
);

const SBLMobileDate = ({ value, label, className, onChange, onBlur, format }) => {
  const inputRef = useRef(null);
  const handleClick = () => {
    if (inputRef.current) inputRef.current.focus();
  };
  return (
    <div className={className}>
      <div className={styles.SBLLabel}>{label}</div>
      <div style={{ position: 'relative' }}>
        <label htmlFor="date" className={styles.SBLMobileDateText} onClick={handleClick}>
          {value.format(format)}
        </label>
        <input
          ref={inputRef}
          type="date"
          id="date"
          value={value.format('YYYY-MM-DD')}
          onChange={onChange}
          className={styles.SBLInput}
          onBlur={onBlur}
        />
      </div>
    </div>
  );
};

const SBLInput = ({ value, label, placeholder, onChange, className, disabled, name, number, inputClassName, ...otherProps }) => (
  <div className={className}>
    <div className={styles.SBLLabel}>{label}</div>
    {number ? (
      <NumberInput
        className={[styles.SBLInput, inputClassName].join(' ')}
        name={name ? name : label}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        {...otherProps}
      />
    ) : (
      <TextInput
        className={[styles.SBLInput, inputClassName].join(' ')}
        name={name ? name : label}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        {...otherProps}
      />
    )}
  </div>
);

const SBLButton = ({ label, onClick, isNegative, isWhite, isDisabled, className }) => {
  const classes = [className, styles.SBLButton];
  if (isNegative) classes.push(styles.negative);
  if (isWhite) classes.push(styles.whiteButton);
  if (isDisabled) classes.push(styles.disabledButton);
  return (
    <div onClick={e => !isDisabled && onClick(e)} className={classes.join(' ')}>
      {label}
    </div>
  );
};

const SBLTable = ({ children, className, noBottomBorder }) => {
  const classes = [className, styles.SBLTable];
  if (noBottomBorder) classes.push(styles.SBLTableBottomBorderless);
  return (
    <div className={classes.join(' ')}>
      <table>{children}</table>
    </div>
  );
};

const SBLTableHead = ({ headers, children, ...otherProps }) => (
  <thead {...otherProps}>
    <tr>
      <th></th>
      {children}
      {headers.map((header, i) => (
        <th key={i} className={`th-${header}`}>
          {header}
        </th>
      ))}
      <th></th>
    </tr>
  </thead>
);

const SBLTableBody = ({ children }) => <tbody>{children}</tbody>;

const SBLTableRow = ({ children, className }) => (
  <tr className={className}>
    <td></td>
    {children}
    <td></td>
  </tr>
);

const SBLAlertIcon = ({ num, className }) => <div className={[className, styles.SBLAlertIcon].join(' ')}>{num}</div>;

const SBLCircleIcon = ({ text, className }) => (
  <div className={[className, styles.SBLCircleIcon].join(' ')}>
    <span>{text}</span>
  </div>
);

const SBLFlexContainer = ({ children, className, ...otherProps }) => (
  <div className={[className, styles.SBLFlexContainer].join(' ')} {...otherProps}>
    {children}
  </div>
);

const SBLSocialButton = ({ type, onClick, className, label, url }) => (
  <div className={[styles.SBLSocialButton, className].join(' ')}>
    {label ? <div className={styles.SBLLabel}>{label}</div> : null}
    <Link href={url}>
      <div
        className={[type, styles.SBLSocialButtonOuter].join(' ')}
        target="_blank"
        onClick={() => {
          onClick(type);
        }}
      >
        <div />
      </div>
    </Link>
  </div>
);

const SBLErrorMessage = ({ message, ...otherProps }) => {
  if (!message) return null;
  return (
    <div className={styles.SBLErrorMessage} {...otherProps}>
      {message}
    </div>
  );
};

const SBLModal = ({
  title,
  isOpen,
  className,
  onExit,
  stickyBottomButton,
  isNegative,
  isWhite,
  onSubmit,
  submitText,
  isNegative2,
  isWhite2,
  onSubmit2,
  submitText2,
  children,
  inviteUrl
}) => {
  const bottomButtonClasses = [styles.modalBottomButton];
  if (stickyBottomButton) bottomButtonClasses.push(styles.modalStickyBottomButton);
  return (
    <Modal isOpen={isOpen} className={[className, styles.SBLModal].join(' ')} title={title}>
      <Clickable className={styles.cursor} onClick={onExit}>
        <i className={styles.close} />
      </Clickable>
      <div className={styles.SBLModalContent}>{children}</div>
      <div className={bottomButtonClasses.join(' ')}>
        {submitText && inviteUrl ? (
          <div className={styles.ButtonWithCopyLink}>
            <SBLButton label={submitText} isNegative={isNegative} isWhite={isWhite} onClick={() => onSubmit()} />
            <SBLCopyButton label="copy link" url={inviteUrl} />
          </div>
        ) : (
          submitText && <SBLButton label={submitText} isNegative={isNegative} isWhite={isWhite} onClick={() => onSubmit()} />
        )}
        {submitText2 && <SBLButton label={submitText2} isNegative={isNegative2} isWhite={isWhite2} onClick={() => onSubmit2()} />}
      </div>
    </Modal>
  );
};

const SBLInfoBlock = ({ label, text, icon, img, className, bold, mobileText }) => (
  <div className={[styles.SBLInfoBlock, className].join(' ')}>
    <div className={styles.SBLLabel}>{label}</div>
    <div className={styles.SBLInfoBlockContent}>
      <div className={styles.SBLInfoBlockIcon}>
        {icon ? <div className={icon}></div> : null}
        {!icon && img && <TeamImage src={img} alt={text} />}
      </div>
      <div style={bold ? { fontWeight: '800' } : null}>
        {text} {mobileText && <span className={styles.hideMobile}>{mobileText}</span>}
      </div>
    </div>
  </div>
);

const SBLInputBlock = ({ label, subLabel, update, children, updateClick, updateVar, saveClick, className, ...otherProps }) => {
  return (
    <div className={[styles.SBLInputBlock, className].join(' ')} {...otherProps}>
      <div className={styles.SBLInputBlockHeader}>
        {label}
        {subLabel && <span className={styles.SBLInputBlockSubLabel}>{subLabel}</span>}
        {update && (
          <div onClick={updateVar ? saveClick : updateClick} className={styles.SBLInputBlockUpdate}>
            {updateVar ? 'save' : 'update'}
          </div>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
};

const SBLLargeInput = ({ className, name, value, onChange, disabled, number, placeholder, otherProps }) => {
  const inputRef = useRef(null);
  useEffect(() => {
    if (!disabled) inputRef.current && inputRef.current.focus();
  }, [disabled, inputRef]);

  return (
    <div className={[styles.SBLLargeInput, className].join(' ')}>
      {number ? (
        <NumberInput
          className={styles.SBLInput}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          forwardRef={inputRef}
          {...otherProps}
        />
      ) : (
        <TextInput
          className={styles.SBLInput}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          ref={inputRef}
          {...otherProps}
        />
      )}
    </div>
  );
};

export {
  SBLContentContainer,
  SBLSelector,
  SBLDateTime,
  SBLMobileDate,
  SBLTeamBlock,
  SBLInput,
  SBLInputBlock,
  SBLLargeInput,
  SBLButton,
  SBLTable,
  SBLTableHead,
  SBLTableBody,
  SBLTableRow,
  SBLAlertIcon,
  SBLCircleIcon,
  SBLModal,
  SBLSocialButton,
  SBLInfoBlock,
  SBLFlexContainer,
  SBLTeamImageGroup,
  SBLErrorMessage
};
